import type { PlayerState } from "@ict/shared";
import { t } from "@/i18n/i18n";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  return e;
}

/** Manages the full-screen pre-game flow: landing -> access code -> name -> loading -> lobby. */
export class ScreenManager {
  private landing: HTMLDivElement;
  private accessInput: HTMLInputElement;
  private accessStatus: HTMLDivElement;
  private nameScreen: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private nameStatus: HTMLDivElement;
  private loadingScreen: HTMLDivElement;
  private loadingFill: HTMLDivElement;
  private loadingDetail: HTMLDivElement;
  private lobbyScreen: HTMLDivElement;
  private lobbyList: HTMLUListElement;
  private lobbyCount: HTMLDivElement;
  private lobbyStatus: HTMLDivElement;

  private screens: HTMLDivElement[];

  constructor(private readonly container: HTMLElement) {
    this.landing = this.buildLanding();
    const access = this.buildAccessCode();
    this.accessInput = access.input;
    this.accessStatus = access.status;

    const name = this.buildNameEntry();
    this.nameScreen = name.screen;
    this.nameInput = name.input;
    this.nameStatus = name.status;

    const loading = this.buildLoading();
    this.loadingScreen = loading.screen;
    this.loadingFill = loading.fill;
    this.loadingDetail = loading.detail;

    const lobby = this.buildLobby();
    this.lobbyScreen = lobby.screen;
    this.lobbyList = lobby.list;
    this.lobbyCount = lobby.count;
    this.lobbyStatus = lobby.status;

    this.screens = [this.landing, access.screen, this.nameScreen, this.loadingScreen, this.lobbyScreen];
    this.screens.forEach((s) => container.appendChild(s));
    this.accessScreen = access.screen;
  }

  private accessScreen!: HTMLDivElement;

  private hideAll(): void {
    this.screens.forEach((s) => s.classList.add("hidden"));
  }

  hideAllScreens(): void {
    this.hideAll();
  }

  // ---- Landing ----
  private buildLanding() {
    const screen = el("div", "screen");
    const title = el("div", "title-xl");
    title.textContent = t("landing.title");
    const sub = el("div", "subtitle");
    sub.textContent = t("landing.subtitle");
    const btn = el("button", "btn");
    btn.textContent = t("landing.enterButton");
    btn.id = "enter-mission-btn";
    screen.append(title, sub, btn);
    return screen;
  }

  showLanding(onEnter: () => void): void {
    this.hideAll();
    this.landing.classList.remove("hidden");
    const btn = this.landing.querySelector("#enter-mission-btn") as HTMLButtonElement;
    const handler = () => onEnter();
    btn.onclick = handler;
  }

  // ---- Access code ----
  private buildAccessCode() {
    const screen = el("div", "screen hidden");
    const title = el("div", "title-xl");
    title.style.fontSize = "1.8rem";
    title.textContent = t("accessCode.title");
    const status = el("div", "status-msg");
    const input = el("input", "field");
    input.type = "text";
    input.maxLength = 24;
    input.autocomplete = "off";
    input.placeholder = t("accessCode.placeholder");
    const btn = el("button", "btn");
    btn.textContent = t("accessCode.confirm");
    screen.append(title, status, input, btn);
    return { screen, input, status, btn };
  }

  showAccessCode(onSubmit: (code: string) => void): void {
    this.hideAll();
    this.accessScreen.classList.remove("hidden");
    this.accessStatus.textContent = "";
    this.accessStatus.className = "status-msg";
    this.accessInput.value = "";
    this.accessInput.focus();
    const btn = this.accessScreen.querySelector("button")!;
    const submit = () => onSubmit(this.accessInput.value);
    btn.onclick = submit;
    this.accessInput.onkeydown = (e) => {
      if (e.key === "Enter") submit();
    };
  }

  showAccessDenied(): void {
    this.accessStatus.textContent = t("accessCode.denied");
    this.accessStatus.className = "status-msg error";
    this.accessInput.value = "";
    this.accessInput.focus();
  }

  showAccessGranted(): void {
    this.accessStatus.textContent = t("accessCode.granted");
    this.accessStatus.className = "status-msg ok";
  }

  // ---- Name entry ----
  private buildNameEntry() {
    const screen = el("div", "screen hidden");
    const title = el("div", "title-xl");
    title.style.fontSize = "1.8rem";
    title.textContent = t("nameEntry.title");
    const status = el("div", "status-msg");
    const input = el("input", "field");
    input.type = "text";
    input.maxLength = 24;
    input.autocomplete = "off";
    input.placeholder = t("nameEntry.placeholder");
    const btn = el("button", "btn");
    btn.textContent = t("nameEntry.join");
    screen.append(title, status, input, btn);
    return { screen, input, status, btn };
  }

  showNameEntry(onSubmit: (name: string) => void, suggested?: string): void {
    this.hideAll();
    this.nameScreen.classList.remove("hidden");
    this.nameStatus.textContent = "";
    this.nameStatus.className = "status-msg";
    this.nameInput.value = suggested ?? "";
    this.nameInput.focus();
    const btn = this.nameScreen.querySelector("button")!;
    const submit = () => {
      const value = this.nameInput.value.trim();
      if (!value) {
        this.nameStatus.textContent = t("nameEntry.emptyError");
        this.nameStatus.className = "status-msg error";
        return;
      }
      onSubmit(value);
    };
    btn.onclick = submit;
    this.nameInput.onkeydown = (e) => {
      if (e.key === "Enter") submit();
    };
  }

  showNameError(message: string): void {
    this.nameStatus.textContent = message;
    this.nameStatus.className = "status-msg error";
  }

  // ---- Loading ----
  private buildLoading() {
    const screen = el("div", "screen hidden");
    const title = el("div", "title-xl");
    title.style.fontSize = "1.6rem";
    title.textContent = t("loading.title");
    const track = el("div", "loading-bar-track");
    const fill = el("div", "loading-bar-fill");
    track.appendChild(fill);
    const detail = el("div", "loading-detail");
    detail.textContent = t("loading.detail");
    screen.append(title, track, detail);
    return { screen, fill, detail };
  }

  showLoading(): void {
    this.hideAll();
    this.loadingScreen.classList.remove("hidden");
  }

  setLoadingProgress(percent: number, detail: string): void {
    this.loadingFill.style.width = `${Math.max(0, Math.min(100, percent))}%`;
    this.loadingDetail.textContent = detail;
  }

  // ---- Lobby ----
  private buildLobby() {
    const screen = el("div", "screen hidden");
    const panel = el("div", "panel lobby-panel");
    const title = el("div", "title-xl");
    title.style.fontSize = "1.8rem";
    title.textContent = t("lobby.title");
    const sub = el("div", "subtitle");
    sub.textContent = t("lobby.connectedPlayers");
    const list = el("ul", "player-list");
    const count = el("div", "lobby-count");
    const status = el("div", "lobby-waiting");
    status.textContent = t("lobby.waiting");
    panel.append(title, sub, list, count, status);
    screen.appendChild(panel);
    return { screen, list, count, status };
  }

  showLobby(): void {
    this.hideAll();
    this.lobbyScreen.classList.remove("hidden");
  }

  updateLobby(players: PlayerState[], requiredPlayers: number, statusText?: string): void {
    const connected = players.filter((p) => p.connected);
    this.lobbyList.innerHTML = connected
      .map(
        (p) =>
          `<li><span>${escapeHtml(p.name)}</span><span class="ready-tag">${p.ready ? t("lobby.ready") : t("lobby.notReady")}</span></li>`
      )
      .join("");
    this.lobbyCount.textContent = `${connected.length} / ${requiredPlayers} ${t("lobby.playersConnectedSuffix")}`;
    if (statusText) this.lobbyStatus.textContent = statusText;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
